export type Quantifier = 'any' | 'all';

export interface FieldSegment {
  field: { name: string };
}
export interface CollectionSegment {
  collection: { name: string; quantifier: Quantifier };
}
export type PathSegment = FieldSegment | CollectionSegment;

export interface FieldPath {
  segments: PathSegment[];
}

export type MassUnit = 'Gram' | 'Kilogram' | 'Ton';
export type LengthUnit = 'Millimeter' | 'Centimeter' | 'Meter' | 'Kilometer';

export type SelfRef = { field: FieldPath } | { label: string };

export type FilterLiteral =
  | 'null'
  | { number: number }
  | { integer: number }
  | { string: string }
  | { bool: boolean }
  | { uid: string }
  | { uid_list: string[] }
  | { mass: { value: number; unit: MassUnit } }
  | { duration: string }
  | { length: { value: number; unit: LengthUnit } }
  | { date: string }
  | { date_time: string }
  | { list: FilterLiteral[] }
  | { self: SelfRef };

export const LITERAL_KINDS = [
  'null',
  'number',
  'integer',
  'string',
  'bool',
  'uid',
  'uid_list',
  'mass',
  'duration',
  'length',
  'date',
  'date_time',
  'list',
  'self'
] as const;
export type LiteralKind = (typeof LITERAL_KINDS)[number];

/** Wire spellings; the DSL spells the numbered ones with an extra underscore (`last_7_days`). */
export const RELATIVE_DATE_KEYWORDS = [
  'today',
  'yesterday',
  'tomorrow',
  'last7_days',
  'last30_days',
  'last3_months',
  'last6_months',
  'last_year',
  'next7_days',
  'next30_days',
  'next3_months',
  'next6_months',
  'next_year'
] as const;
export type RelativeDateKeyword = (typeof RELATIVE_DATE_KEYWORDS)[number];
export type RelativeDateSpec = RelativeDateKeyword | { within: number };

export interface LiteralRange {
  lo: FilterLiteral;
  hi: FilterLiteral;
}
export interface LabelLiteral {
  label: string;
  value: FilterLiteral;
}
export interface LabelLiterals {
  label: string;
  values: FilterLiteral[];
}
export interface LabelRange {
  label: string;
  lo: FilterLiteral;
  hi: FilterLiteral;
}
export interface LabelText {
  label: string;
  value: string;
}

export interface OpArgumentByShape {
  literal: FilterLiteral;
  range: LiteralRange;
  literals: FilterLiteral[];
  relative_date: RelativeDateSpec;
  text: string;
  texts: string[];
  label_literal: LabelLiteral;
  label_literals: LabelLiterals;
  label_range: LabelRange;
  label_text: LabelText;
}
export type OpShape = 'none' | keyof OpArgumentByShape;
export type OpArgument = OpArgumentByShape[keyof OpArgumentByShape];

export const FILTER_OP_SHAPES = {
  is_empty: 'none',
  is_not_empty: 'none',
  equals: 'literal',
  not_equals: 'literal',
  greater_than: 'literal',
  greater_than_or_equal: 'literal',
  less_than: 'literal',
  less_than_or_equal: 'literal',
  between: 'range',
  is_any_of: 'literals',
  is_none_of: 'literals',
  contains_all: 'literals',
  not_contains_all: 'literals',
  relative_date: 'relative_date',
  contains: 'text',
  not_contains: 'text',
  starts_with: 'text',
  ends_with: 'text',
  matches_regex: 'text',
  has_tag: 'text',
  not_has_tag: 'text',
  has_any_tag: 'texts',
  has_all_tags: 'texts',
  has_none_of_tags: 'texts',
  has_label: 'text',
  not_has_label: 'text',
  label_equals: 'label_literal',
  label_not_equals: 'label_literal',
  label_in: 'label_literals',
  label_not_in: 'label_literals',
  label_greater_than: 'label_literal',
  label_greater_than_or_equal: 'label_literal',
  label_less_than: 'label_literal',
  label_less_than_or_equal: 'label_literal',
  label_between: 'label_range',
  label_contains: 'label_text',
  label_starts_with: 'label_text',
  label_ends_with: 'label_text',
  label_matches_regex: 'label_text',
  label_references: 'label_literal',
  label_references_any_of: 'label_literals',
  label_references_none_of: 'label_literals'
} as const satisfies Record<string, OpShape>;

export type FilterOpName = keyof typeof FILTER_OP_SHAPES;
type ShapeOf<K extends FilterOpName> = (typeof FILTER_OP_SHAPES)[K];
export type UnitOpName = {
  [K in FilterOpName]: ShapeOf<K> extends 'none' ? K : never;
}[FilterOpName];
type TaggedOpName = Exclude<FilterOpName, UnitOpName>;
export type FilterOp =
  | UnitOpName
  | {
      [K in TaggedOpName]: {
        [P in K]: OpArgumentByShape[Exclude<ShapeOf<K>, 'none'> & keyof OpArgumentByShape];
      };
    }[TaggedOpName];

export interface FilterPredicate {
  path: FieldPath;
  op: FilterOp;
}

export interface AssociationFilter {
  edge: string;
  quantifier: Quantifier;
  filter: FilterExpr;
}

export type FilterExpr =
  | { and: FilterExpr[] }
  | { or: FilterExpr[] }
  | { not: FilterExpr }
  | { predicate: FilterPredicate }
  | { association: AssociationFilter };

export const COMPAT_TARGETS = ['vehicle', 'driver', 'trailer', 'site', 'job'] as const;
export type CompatTarget = (typeof COMPAT_TARGETS)[number];
export type RoutePosition = 'first' | 'last';

export type CompatibilityCondition =
  | { kind: 'context'; filter: FilterExpr }
  | { kind: 'route_position'; positions: RoutePosition[] }
  | { kind: 'service_time_window'; start: string; end: string };

export interface CompatibilityRequirement {
  target_entity_type: CompatTarget;
  filter: FilterExpr;
  violation_penalty?: number | null;
  conditions?: CompatibilityCondition[];
}

export interface CompatibilityCfg {
  all_of: CompatibilityRequirement[];
}

export function isFilterOpName(name: string): name is FilterOpName {
  return Object.prototype.hasOwnProperty.call(FILTER_OP_SHAPES, name);
}

export function opName(op: FilterOp): FilterOpName {
  return typeof op === 'string' ? op : (Object.keys(op)[0] as FilterOpName);
}

export function opArgument(op: FilterOp): OpArgument | null {
  return typeof op === 'string' ? null : (Object.values(op)[0] as OpArgument);
}

export function makeOp(name: FilterOpName, arg: OpArgument | null): FilterOp {
  if (FILTER_OP_SHAPES[name] === 'none') return name as UnitOpName;
  return { [name]: arg } as unknown as FilterOp;
}

export function literalKind(literal: FilterLiteral): LiteralKind {
  return literal === 'null' ? 'null' : (Object.keys(literal)[0] as LiteralKind);
}

/**
 * Where an association sits: its edge at the root, `parent/edge` inside another association's
 * filter. A root edge is its own path, so callers that only know one hop pass the edge as is.
 */
export function edgePath(parent: string | null, edge: string): string {
  return parent === null ? edge : `${parent}/${edge}`;
}

export function fieldPath(...names: string[]): FieldPath {
  return { segments: names.map((name) => ({ field: { name } })) };
}

export function segmentName(segment: PathSegment): string {
  return 'field' in segment ? segment.field.name : segment.collection.name;
}

/** Identity of a path for lookups: segment names and collection flags, quantifiers ignored. */
export function pathKey(path: FieldPath): string {
  return JSON.stringify(
    path.segments.map((segment) =>
      'collection' in segment ? [segment.collection.name] : segment.field.name
    )
  );
}
